import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { AbsenceModalComponent } from 'src/app/absences/absence-form/absence-form.component';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import { AgreementToPick } from 'src/app/models/dtos/agreement-to-pick-dto';
import { Location } from '@angular/common';
import { AbsenceService } from 'src/app/data/absence.service';
import { AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { SubSink } from 'SubSink';

export enum AbsenceTabsType {
  Absences = 'Absences',
  AbsenceLimits = 'AbsenceLimits',
  Illness = 'SickLeaves',
}

@Component({
  selector: 'app-absences',
  templateUrl: './absences.component.html',
  styleUrls: ['./absences.component.scss'],
  standalone: false
})
export class AbsencesComponent implements OnChanges, OnDestroy {
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private workerAgreementService = inject(WorkerAgreementService);
  private router = inject(Router);
  private location = inject(Location);
  private absenceService = inject(AbsenceService);

  @Input() workerId: string;
  @Input() absenceId?: string;
  @Input() tabType: AbsenceTabsType;
  @ViewChild(MatTabGroup, { static: true }) tabGroup: MatTabGroup;

  navigation = [
    {
      listType: AbsenceTabsType.AbsenceLimits,
      guardPredicate: () => this.workerAgreement?.HasAbsences,
      link: 'limits',
    },
    {
      listType: AbsenceTabsType.Absences,
      guardPredicate: () => this.workerAgreement?.HasAbsences,
      link: '../absences',
    },
    {
      listType: AbsenceTabsType.Illness,
      guardPredicate: () => this.workerAgreement?.HasSickLeaves,
      link: 'illness',
    },
  ];
  workerAgreement: AgreementToPick;
  agreements: Observable<AgreementToPick[]>;
  refreshToken: number;
  private selectedNavigation;
  private readonly subs = new SubSink();

  constructor() { }

  async ngOnChanges(): Promise<void> {
    this.agreements = !!this.workerId ? this.workerAgreementService.getAgreementsByWorkerId(+this.workerId) : of([]);

    if (this.absenceId) {
      this.openModal();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  changeRefreshToken = (token: number) => (this.refreshToken = token);

  onAgreementSelection = () => this.setSelectedTabFromUrl();

  openAbsenceModal = async (absence: AbsenceDto = null) => {
    await firstValueFrom(this.dialog
      .open(AbsenceModalComponent, {
        maxHeight: '90vh',
        data: this.absenceModalData(absence),
      })
      .afterClosed()
      .pipe(first())
      .pipe(tap((isModified: boolean) => {
        if (isModified) {
          this.refreshToken = Math.random();
        }
      })))
  };

  private absenceModalData = (absence: AbsenceDto = null) => ({
    WorkerId: +this.workerId,
    Absence: absence,
    WorkerAgreementId: this.workerAgreement.Id,
    EmploymentTypeId: this.workerAgreement.EmploymentTypeId,
    HasNextYearTimeLogging: this.workerAgreement.HasNextYearTimeLogging,
  });

  private setSelectedTabFromUrl(): void {
    let index = this.navigation.findIndex(n => n.listType === this.tabType && n.guardPredicate());

    if (index < 0) {
      index = this.navigation.findIndex(n => n.guardPredicate());
    }

    this.tabGroup.selectedIndex = index;
  }

  onTabChanged(event: MatTabChangeEvent): void {
    this.selectedNavigation = this.navigation.filter(n => n.guardPredicate())[event.index];

    this.setUrl();
  }

  private openModal() {
    const absence = this.absenceService.getAbsence(+this.absenceId);

    this.subs.sink = combineLatest([this.agreements, absence])
      .subscribe(async ([agreements, absence]) => {
        this.workerAgreement = agreements.find(a => a.Id === absence.WorkerAgreementId);

        if (absence && this.workerAgreement) {
          await this.openAbsenceModal(absence);
          this.absenceId = undefined;
          this.setUrl();
        }
      });
  }

  private setUrl() {
    if (this.selectedNavigation) {
      this.tabType = this.selectedNavigation.listType;
      const command = [this.selectedNavigation.link]

      if (this.absenceId) {
        command.push(this.absenceId);
      }

      const url = this.router.createUrlTree(command, { relativeTo: this.route.parent }).toString()
      this.location.go(url);
    }
  }
}
